'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { useAllTeams } from '@/hooks/useAllTeams'
import type { Team } from '@/types/team'

export interface SupervisorAvailableAgent {
  id: number
  username: string
  firstName?: string | null
  lastName?: string | null
  teamIds?: number[]
  primaryTeamId?: number | null
  teamId?: number | null
  teamName?: string | null
  isOnline?: boolean | null
}

interface SessionSummary {
  id?: string | null
  platform?: string | null
  assignedAgentName?: string | null
  assignedTeamId?: number | null
  assignedTeamName?: string | null
  assignedAgentId?: number | null
  assignedAgentNumber?: string | null
  pendingHandoverRequest?: {
    teamId?: number | null
    teamIds?: number[] | null
    priority?: string | null
    requestType?: string | null
    requestedAt?: string | null
    reason?: string | null
    teamName?: string | null
    notes?: string | null
    assignedTo?: number | null
    assignedToName?: string | null
  } | null
  lastAssignment?: {
    status?: string | null
    assignedTo?: number | null
    assignedAt?: string | null
    completedAt?: string | null
    updatedAt?: string | null
    teamId?: number | null
    teamName?: string | null
    agentId?: number | null
    agentName?: string | null
  } | null
}

export interface SupervisorManageDialogSubmit {
  teamId: number
  agentId: number | null
  notes?: string
}

interface SupervisorManageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionSummary?: SessionSummary | null
  myTeamIds?: Array<number | string | null> | null
  agents: SupervisorAvailableAgent[]
  isLoadingAgents: boolean
  agentsError?: string | null
  onSubmit: (payload: SupervisorManageDialogSubmit) => Promise<void> | void
  isSubmitting: boolean
  isTakeoverPending?: boolean
  actionError?: string | null
  defaultTeamId?: number | null
  actionLabel?: string
}

export function SupervisorManageDialog(props: SupervisorManageDialogProps) {
  const dialogKey = `${props.sessionSummary?.id ?? 'none'}-${props.defaultTeamId ?? 'none'}-${props.open ? 'open' : 'closed'}`
  return <SupervisorManageDialogContent key={dialogKey} {...props} />
}

function SupervisorManageDialogContent({
  open,
  onOpenChange,
  sessionSummary,
  myTeamIds,
  agents,
  isLoadingAgents,
  agentsError,
  onSubmit,
  isSubmitting,
  isTakeoverPending = false,
  actionError = null,
  defaultTeamId = null,
  actionLabel = 'Reassign',
}: SupervisorManageDialogProps) {
  const myTeamIdSet = useMemo(() => {
    if (!Array.isArray(myTeamIds)) {
      return new Set<number>()
    }
    const normalized = myTeamIds
      .map(id => {
        if (id == null) {
          return null
        }
        const parsed = Number.parseInt(String(id), 10)
        return Number.isNaN(parsed) ? null : parsed
      })
      .filter((id): id is number => id != null)
    return new Set(normalized)
  }, [myTeamIds])

  const {
    teams: allTeamsData,
    isFetching: isFetchingTeams,
    error: teamsError,
  } = useAllTeams({ enabled: open })

  const teamOptions = useMemo(() => {
    if (!Array.isArray(allTeamsData)) {
      return [] as Team[]
    }
    return [...allTeamsData]
      .filter(team => team && team.isActive !== false)
      .sort((teamA, teamB) => {
        const aMine = myTeamIdSet.has(teamA.id)
        const bMine = myTeamIdSet.has(teamB.id)
        if (aMine !== bMine) {
          return aMine ? -1 : 1
        }
        return teamA.name.localeCompare(teamB.name, undefined, {
          sensitivity: 'base',
        })
      })
  }, [allTeamsData, myTeamIdSet])

  const availableTeamIds = useMemo(
    () => new Set(teamOptions.map(team => team.id)),
    [teamOptions]
  )

  const teamsErrorMessage = useMemo(() => {
    if (!teamsError) {
      return null
    }
    if (teamsError instanceof Error) {
      return teamsError.message
    }
    return String(teamsError)
  }, [teamsError])
  const requestedTeamId = useMemo(() => {
    const request = sessionSummary?.pendingHandoverRequest
    if (request?.teamId != null) {
      const parsed = Number.parseInt(String(request.teamId), 10)
      if (!Number.isNaN(parsed)) {
        return parsed
      }
    }
    if (Array.isArray(request?.teamIds) && request.teamIds.length > 0) {
      const parsed = Number.parseInt(String(request.teamIds[0]), 10)
      if (!Number.isNaN(parsed)) {
        return parsed
      }
    }
    const assignedTeamId = sessionSummary?.assignedTeamId
    if (assignedTeamId != null) {
      const parsed = Number.parseInt(String(assignedTeamId), 10)
      if (!Number.isNaN(parsed)) {
        return parsed
      }
    }
    if (sessionSummary?.lastAssignment?.teamId != null) {
      const parsed = Number.parseInt(
        String(sessionSummary.lastAssignment.teamId),
        10
      )
      if (!Number.isNaN(parsed)) {
        return parsed
      }
    }
    return null
  }, [sessionSummary])
  const defaultAgentId = useMemo(() => {
    const candidates: Array<number | string | null | undefined> = [
      sessionSummary?.assignedAgentId,
      sessionSummary?.pendingHandoverRequest?.assignedTo,
      sessionSummary?.lastAssignment?.assignedTo,
      sessionSummary?.lastAssignment?.agentId,
    ]
    for (const value of candidates) {
      if (value == null) {
        continue
      }
      const parsed = Number.parseInt(String(value), 10)
      if (!Number.isNaN(parsed)) {
        return parsed
      }
    }
    return null
  }, [sessionSummary])
  const requestedTeamName = useMemo(() => {
    const requestName = sessionSummary?.pendingHandoverRequest?.teamName
    if (requestName && requestName.trim().length > 0) {
      return requestName
    }
    const assignedName = sessionSummary?.assignedTeamName
    if (assignedName && assignedName.trim().length > 0) {
      return assignedName.trim()
    }
    const assignmentName = sessionSummary?.lastAssignment?.teamName
    if (assignmentName && assignmentName.trim().length > 0) {
      return assignmentName
    }
    return null
  }, [sessionSummary])
  const requestedTeamInOptions =
    requestedTeamId != null && availableTeamIds.has(requestedTeamId)
  const resolvedDefaultTeamId = useMemo(() => {
    if (requestedTeamInOptions) {
      return requestedTeamId
    }
    if (defaultTeamId != null && availableTeamIds.has(defaultTeamId)) {
      return defaultTeamId
    }
    if (teamOptions.length === 1) {
      return teamOptions[0].id
    }
    return null
  }, [
    availableTeamIds,
    defaultTeamId,
    requestedTeamId,
    requestedTeamInOptions,
    teamOptions,
  ])

  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(
    resolvedDefaultTeamId
  )
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(
    defaultAgentId
  )
  const [notes, setNotes] = useState('')
  const safeSelectedTeamId = useMemo(() => {
    if (selectedTeamId != null && availableTeamIds.has(selectedTeamId)) {
      return selectedTeamId
    }
    return resolvedDefaultTeamId
  }, [availableTeamIds, resolvedDefaultTeamId, selectedTeamId])

  useEffect(() => {
    if (!open) {
      return
    }

    console.log('[SupervisorManageDialog] Debug props snapshot:', {
      sessionSummary,
      defaultTeamId,
      myTeamIds,
      myTeamIdSet: Array.from(myTeamIdSet.values()),
      teamOptions: teamOptions.map(team => `${team.name} - ${team.id}`),
    })
  }, [defaultTeamId, myTeamIdSet, myTeamIds, open, sessionSummary, teamOptions])

  const filteredAgents = useMemo(() => {
    if (!safeSelectedTeamId) {
      return []
    }

    return agents.filter(agent => {
      if (Array.isArray(agent.teamIds) && agent.teamIds.length > 0) {
        return agent.teamIds.includes(safeSelectedTeamId)
      }
      if (agent.primaryTeamId != null) {
        return agent.primaryTeamId === safeSelectedTeamId
      }
      if (agent.teamId != null) {
        return agent.teamId === safeSelectedTeamId
      }
      return false
    })
  }, [agents, safeSelectedTeamId])

  const filteredAgentsOnlineCount = useMemo(
    () => filteredAgents.filter(agent => Boolean(agent.isOnline)).length,
    [filteredAgents]
  )

  const safeSelectedAgentId = useMemo(() => {
    if (
      selectedAgentId != null &&
      filteredAgents.some(agent => agent.id === selectedAgentId)
    ) {
      return selectedAgentId
    }
    if (
      defaultAgentId != null &&
      filteredAgents.some(agent => agent.id === defaultAgentId)
    ) {
      return defaultAgentId
    }
    return null
  }, [defaultAgentId, filteredAgents, selectedAgentId])

  const handleSubmit = useCallback(async () => {
    if (!safeSelectedTeamId || isSubmitting || isTakeoverPending) {
      return
    }

    await onSubmit({
      teamId: safeSelectedTeamId,
      agentId: safeSelectedAgentId,
      notes: notes.trim() ? notes.trim() : undefined,
    })
  }, [
    isSubmitting,
    isTakeoverPending,
    notes,
    onSubmit,
    safeSelectedAgentId,
    safeSelectedTeamId,
  ])

  const sessionDisplayId = useMemo(() => {
    const id = sessionSummary?.id
    if (!id) {
      return '—'
    }
    return id.length >= 8 ? id.slice(-8) : id
  }, [sessionSummary])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        key={`${sessionSummary?.id ?? 'none'}-${resolvedDefaultTeamId ?? 'none'}-${open ? 'open' : 'closed'}`}
        className="max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>Manage Session</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {typeof actionError === 'string' && actionError.trim().length > 0 && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {actionError.trim()}
            </div>
          )}

          <div className="space-y-1 text-sm text-gray-600">
            <p className="font-medium text-gray-900">
              Session {sessionDisplayId}
            </p>
            <p>
              Current assignee:{' '}
              {sessionSummary.assignedAgentName &&
              sessionSummary.assignedAgentName.trim().length > 0
                ? sessionSummary.assignedAgentName
                : 'Unassigned'}
            </p>
            <p>
              Platform:{' '}
              {sessionSummary.platform
                ? sessionSummary.platform.toUpperCase()
                : '—'}
            </p>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="supervisor-manage-team"
              className="text-sm font-medium text-gray-900"
            >
              Select Team <span className="text-red-500">*</span>
            </Label>
            <Select
              value={
                safeSelectedTeamId != null
                  ? String(safeSelectedTeamId)
                  : '__none'
              }
              onValueChange={value => {
                if (!value || value === '__none') {
                  setSelectedTeamId(null)
                  setSelectedAgentId(null)
                  return
                }
                if (
                  value === '__loading' ||
                  value === '__empty' ||
                  value === '__requested'
                ) {
                  return
                }
                const parsed = Number.parseInt(value, 10)
                if (Number.isNaN(parsed)) {
                  setSelectedTeamId(null)
                  setSelectedAgentId(null)
                  return
                }
                setSelectedTeamId(parsed)
                setSelectedAgentId(null)
              }}
            >
              <SelectTrigger
                id="supervisor-manage-team"
                disabled={teamOptions.length === 0 && !isFetchingTeams}
              >
                <SelectValue placeholder="Choose a team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">-- Select team --</SelectItem>
                {isFetchingTeams ? (
                  <SelectItem value="__loading" disabled>
                    Loading teams...
                  </SelectItem>
                ) : teamOptions.length > 0 ? (
                  teamOptions.map(team => {
                    const isMine = myTeamIdSet.has(team.id)
                    const label = `${team.name} ${isMine ? '(My Team)' : '(Other Team)'}`
                    return (
                      <SelectItem key={team.id} value={String(team.id)}>
                        {label}
                      </SelectItem>
                    )
                  })
                ) : (
                  <SelectItem value="__empty" disabled>
                    No teams available
                  </SelectItem>
                )}
                {requestedTeamId != null && !requestedTeamInOptions && (
                  <SelectItem value="__requested" disabled>
                    {requestedTeamName
                      ? `Requested team: ${requestedTeamName} (not in your teams)`
                      : `Requested team ID ${requestedTeamId} (not in your teams)`}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              {teamOptions.length > 0
                ? 'Choose which team should take ownership of this session.'
                : isFetchingTeams
                  ? 'Loading teams...'
                  : 'No teams are currently available for assignment.'}
            </p>
            {requestedTeamId != null && !requestedTeamInOptions && (
              <p className="text-xs text-amber-600">
                {requestedTeamName
                  ? `Request targets ${requestedTeamName}, but you don't have access to that team. Pick an available team to continue.`
                  : `Request targets team ID ${requestedTeamId}, but you don't have access to that team. Pick an available team to continue.`}
              </p>
            )}
            {teamsErrorMessage && (
              <p className="text-xs text-red-600">{teamsErrorMessage}</p>
            )}
          </div>

          {safeSelectedTeamId != null && (
            <div className="space-y-2">
              <Label
                htmlFor="supervisor-manage-agent"
                className="text-sm font-medium text-gray-900"
              >
                Select Agent
              </Label>
              <Select
                value={
                  safeSelectedAgentId != null
                    ? String(safeSelectedAgentId)
                    : '__all'
                }
                onValueChange={value => {
                  if (!value || value === '__all') {
                    setSelectedAgentId(null)
                    return
                  }
                  if (value === '__loading' || value === '__empty') {
                    return
                  }
                  const parsed = Number.parseInt(value, 10)
                  if (Number.isNaN(parsed)) {
                    setSelectedAgentId(null)
                    return
                  }
                  setSelectedAgentId(parsed)
                }}
              >
                <SelectTrigger id="supervisor-manage-agent">
                  <SelectValue placeholder="All agents (broadcast)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All agents (broadcast)</SelectItem>
                  {isLoadingAgents ? (
                    <SelectItem value="__loading" disabled>
                      Loading agents...
                    </SelectItem>
                  ) : filteredAgents.length > 0 ? (
                    filteredAgents.map(agent => {
                      const fullName =
                        agent.firstName && agent.lastName
                          ? `${agent.firstName} ${agent.lastName}`
                          : agent.username
                      const usernamePart =
                        agent.firstName && agent.lastName
                          ? ` (${agent.username})`
                          : ''
                      const statusLabel =
                        agent.isOnline == null
                          ? ''
                          : agent.isOnline
                            ? ' · Online'
                            : ' · Offline'

                      return (
                        <SelectItem key={agent.id} value={String(agent.id)}>
                          {`${fullName}${usernamePart}${statusLabel}`}
                        </SelectItem>
                      )
                    })
                  ) : (
                    <SelectItem value="__empty" disabled>
                      No agents available in this team
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {safeSelectedAgentId == null
                  ? filteredAgents.length > 0
                    ? filteredAgentsOnlineCount > 0
                      ? `Broadcast to every agent in the selected team. ${filteredAgentsOnlineCount} ${filteredAgentsOnlineCount === 1 ? 'agent is' : 'agents are'} online right now.`
                      : 'Broadcast to every agent in the selected team. Everyone is currently offline, but they will be notified when they come online.'
                    : 'No agents are assigned to this team yet.'
                  : 'Assign directly to the selected agent.'}
              </p>
              {agentsError && (
                <p className="text-xs text-red-600">{agentsError}</p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label
              htmlFor="supervisor-manage-notes"
              className="text-sm font-medium text-gray-900"
            >
              Notes (optional)
            </Label>
            <Textarea
              id="supervisor-manage-notes"
              value={notes}
              onChange={event => setNotes(event.target.value)}
              placeholder="Add context or instructions for the receiving team"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex w-full sm:w-auto gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                isTakeoverPending ||
                isLoadingAgents ||
                isFetchingTeams ||
                selectedTeamId === null
              }
            >
              {isSubmitting ? `${actionLabel}...` : actionLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
